import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { apiUrl } from "./LoginSignup";

const BankManagement = () => {
  const [banks, setBanks] = useState([]);
  const [newBankName, setNewBankName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankName, setBankName] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);
  const userSession = JSON.parse(localStorage.getItem("userSession"));

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${apiUrl}/banks/api/banks`, {
        headers: {
          "Content-Type": "application/json",
          authorization: `${userSession.token}`,
        },
      });
      setBanks(response.data);
    } catch (error) {
      console.error("Error fetching banks", error);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const handleAddBank = async (e) => {
    e.preventDefault();
    if (!newBankName.trim()) {
      enqueueSnackbar("Bank name cannot be empty", { variant: "error" });
      return;
    }

    try {
      await axios.post(
        `${apiUrl}/banks/api/banks`,
        { name: newBankName },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `${userSession.token}`,
          },
        }
      );

      await fetchBanks();
      enqueueSnackbar("Bank added successfully!", { variant: "success" });
      setNewBankName("");
    } catch (error) {
      enqueueSnackbar(
        `${error.response?.data?.message || "Error adding bank"}`,
        { variant: "error" }
      );
    }
  };

  const handleEditClick = (bank) => {
    setIsEditing(true);
    setSelectedBank(bank._id);
    setBankName(bank.name);
  };

  const handleSave = async () => {
    try {
      await axios.patch(
        `${apiUrl}/banks/api/banks/${selectedBank}`,
        { name: bankName },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `${userSession.token}`,
          },
        }
      );

      await fetchBanks();
      enqueueSnackbar("Bank updated successfully!", { variant: "success" });
      setIsEditing(false);
      setSelectedBank(null);
    } catch (error) {
      enqueueSnackbar(
        `${error.response?.data?.message || "Error updating bank"}`,
        { variant: "error" }
      );
    }
  };

  const confirmDelete = async () => {
    if (!bankToDelete) return;

    try {
      await axios.delete(`${apiUrl}/banks/api/banks/${bankToDelete}`, {
        headers: {
          "Content-Type": "application/json",
          authorization: `${userSession.token}`,
        },
      });

      await fetchBanks();
      enqueueSnackbar("Bank deleted successfully!", { variant: "success" });
      setIsDeleteModalOpen(false);
      setBankToDelete(null);
    } catch (error) {
      enqueueSnackbar(
        `${error.response?.data?.message || "Error deleting bank"}`,
        { variant: "error" }
      );
    }
  };

  const toggleBankStatus = async (bank) => {
    try {
      await axios.patch(
        `${apiUrl}/banks/api/banks/${bank._id}`,
        { status: !bank.status },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `${userSession.token}`,
          },
        }
      );

      await fetchBanks();
      enqueueSnackbar(
        `Bank ${bank.status ? "disabled" : "enabled"} successfully!`,
        { variant: "success" }
      );
    } catch (error) {
      enqueueSnackbar(
        `${error.response?.data?.message || "Error toggling bank status"}`,
        { variant: "error" }
      );
    }
  };

  const handleDeleteClick = (bank) => {
    setBankToDelete(bank._id);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setBankToDelete(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Add Bank
      </Typography>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <form onSubmit={handleAddBank} style={{ display: "flex", gap: "8px" }}>
          <TextField
            label="Bank Name"
            value={newBankName}
            onChange={(e) => setNewBankName(e.target.value)}
            required
          />
          <Button variant="contained" color="primary" type="submit">
            Add Bank
          </Button>
        </form>
      </Box>
      <Typography variant="h4" align="center" gutterBottom>
        Banks List
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search Banks"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
        />
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>No</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banks
              .filter((bank) =>
                bank.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((bank, index) => (
                <TableRow key={bank._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{bank.name}</TableCell>
                  <TableCell>{bank.status ? "Enabled" : "Disabled"}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleEditClick(bank)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color={bank.status ? "warning" : "success"}
                        size="small"
                        onClick={() => toggleBankStatus(bank)}
                      >
                        {bank.status ? "Disable" : "Enable"}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(bank)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={isEditing} onClose={() => setIsEditing(false)} fullWidth>
        <DialogTitle>Edit Bank</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
          <Button
            onClick={() => {
              setIsEditing(false);
              setSelectedBank(null);
            }}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this bank? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
          <Button onClick={closeDeleteModal} color="secondary" variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankManagement;
